<template>
  <loading-page
    v-if="gettingStorageKeys"
    full
  />
  <div v-else>
    <div
      :class="{
        'default-table':
          currentBreakPoint === 'lg' || currentBreakPoint === 'xl',
      }"
    >
      <div class="px-1 pb-1 pt-2 d-flex">
        <div class="text-md text-main text-thick">
          Storage Keys
        </div>
        <span
          class="text-primary ml-1 cursor-pointer"
          @click="initGenerateStorageKey"
        >
          + Generate New
        </span>
      </div>
      <div class="position-relative h-100 d-none d-lg-block">
        <b-table
          ref="table"
          :items="storageKeys"
          :fields="fields"
          :class="{ loaded: !gettingStorageKeys }"
          show-empty
          responsive
          class="more-visible"
        >
          <template #empty>
            <div
              v-if="gettingStorageKeys"
              class="loading-table text-center py-3"
            >
              <b-spinner variant="primary" />
            </div>
            <no-data
              v-else
              no-data-text="You haven't created any Storage keys yet..."
              create-text="+ Create Storage key"
              access-page="Storage Keys"
              icon="Security"
              :has-permissions="hasPermissions"
              @action="initGenerateStorageKey"
            />
          </template>
          <template #cell(accessKey)="data">
            {{ data.item.accessKey || data.item.username }}
          </template>
          <template #cell(id)="data">
            <a
              v-if="deletingStorageKey !== data.item.name"
              @click="deleteStorageKey(data.item)"
            >
              <GjIcon
                class="icon-right"
                name="Delete"
              />
            </a>
            <b-spinner
              v-else
              small
            />
          </template>
        </b-table>
      </div>

      <div class="d-lg-none visible">
        <template v-for="storageKey in storageKeys">
          <card
            :key="storageKey.id"
            :fields="fields"
            :item="storageKey"
          >
            <template #options>
              <b-dropdown
                id="dropdown-1"
                right
                variant="white"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="deletingStorageKey !== storageKey.name"
                  @click="deleteStorageKey(storageKey)"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>

            <template #accessKey>
              {{ storageKey.accessKey || storageKey.username }}
            </template>
          </card>
        </template>
        <div
          v-if="gettingStorageKeys"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!gettingStorageKeys && storageKeys.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="You haven't created any Storage keys yet..."
              create-text="+ Create Storage key"
              access-page="Storage Keys"
              icon="Security"
              :has-permissions="hasPermissions"
              @action="initGenerateStorageKey"
            />
          </paper-simple>
        </template>
      </div>
    </div>
    <b-modal
      v-model="newKeyModal"
      title="Generate Storage Key"
      ok-variant="outline-primary"
      ok-title="Close"
      body-class="p-0 position-relative"
      ok-only
      hide-footer
      centered
      @hidden="() => {}"
      @ok.prevent="() => {}"
    >
      <div class="p-2">
        <template v-if="!generatedNewKey">
          <input-field
            label="Name*"
            label-position="top"
          >
            <b-input
              v-model="newKey.name"
              placeholder="Enter key name here..."
              type="text"
            />
          </input-field>
          <custom-input-radio
            v-model="newKey.type"
            label="Type"
            :options="keyTypeOptions"
          />
          <b-button
            variant="bg-primary"
            class="w-100"
            :disabled="!!!newKey.name || creatingStorageKey"
            @click="generateStorageKey"
          >
            <span v-if="creatingStorageKey">
              <b-spinner small />

              Generating
            </span>
            <span v-else>
              Generate Storage Key
            </span>
          </b-button>
        </template>
        <template v-else>
          <input-field
            name="storage-secret-key"
            label="Secret Key"
            label-position="top"
          >
            <b-input-group>
              <template #append>
                <b-button
                  v-clipboard:copy="secretKey"
                  v-clipboard:success="copySuccess"
                  variant="bg-gray"
                  class="border-left-0"
                >
                  <span>
                    <GjIcon name="Copy" />
                  </span>
                </b-button>
              </template>
              <b-form-input
                id="storage-secret-key"
                :value="secretKey"
                class="mr-0"
                readonly
              />
            </b-input-group>
          </input-field>
          <custom-alert
            icon-name="InfoAlt"
            icon-color="#e34850"
          >
            {{ warningMessage }}
          </custom-alert>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import InputField from '@/components/form/InputField.vue'
import helpers from '@/mixins/helpers'

export default {
  name: 'StorageKeys',
  components: {
    CustomInputRadio,
    InputField,
    Card,
  },
  mixins: [helpers],
  props: {},
  data() {
    return {
      warningMessage:
        'Copy and save the secret key somewhere secure. This is the only time you will have access to this secret key.',
      newKeyModal: false,
      generatedNewKey: false,
      secretKey: '',
      newKey: {
        name: '',
        type: 's3',
      },
      keyTypeOptions: [
        {
          text: 'S3',
          value: 's3',
        },
        {
          text: 'Swift',
          value: 'swift',
        },
      ],
      fields: [
        { key: 'name', isPrimary: true },
        { key: 'accessKey', label: 'Access Key / Username', isPrimary: true },
        'type',
        { key: 'id', label: 'Actions', hide: true },
      ],
      hasPermissions: true,
    }
  },
  computed: {
    storageKeys() {
      return this.$store.state.storageKeys.storageKeys
    },
    gettingStorageKeys() {
      return this.$store.state.storageKeys.gettingStorageKeys
    },
    creatingStorageKey() {
      return this.$store.state.storageKeys.creatingStorageKey
    },
    deletingStorageKey() {
      return this.$store.state.storageKeys.deletingStorageKey
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
  },
  created() {
    this.fetchStorageApiKeys()
  },
  methods: {
    fetchStorageApiKeys() {
      this.$store.dispatch('storageKeys/getStorageKeys').catch(error => {
        const { status } = error.response || null
        if (status === 424) {
          this.$store.commit('buckets/SET_SHOW_INTRO', true)
          return
        }
        if (status === 426 || status === 428) {
          this.$store.commit('buckets/SET_SERVICE_UNAVAILABLE', status === 428)
          return
        }
        if (status === 403) {
          this.hasPermissions = false
          return
        }
        this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching buckets.')
      })
    },
    initGenerateStorageKey() {
      this.newKeyModal = true
      this.generatedNewKey = false
    },
    generateStorageKey() {
      this.$store
        .dispatch('storageKeys/createStorageKey', this.newKey)
        .then(res => {
          this.generatedNewKey = true
          this.secretKey = res.data.result.secretKey
          this.successToast('Storage key generated successfully!')
          this.fetchStorageApiKeys()
        })
        .catch(err => {
          this.errorToast(err.response?.data?.errors[0] || 'Something went wrong while creating storage key.')
        })
    },
    deleteStorageKey(key) {
      this.confirmDelete(
        'Delete Storage Key',
        `Are you sure you want to delete "${key.name}" storage key?`,
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('storageKeys/deleteStorageKey', key)
            .then(() => {
              this.successToast('Storage key deleted successfully!')
            })
            .catch(err => {
              this.errorToast(err.response?.data?.errors[0] || 'Something went wrong while deleting storage key.')
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.apikeys {
  .default-table {
    box-shadow: none;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 0px 2px 8px rgb(5 45 97 / 8%);
    border-radius: 4px;
    .table-responsive {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-direction: column;
      ::v-deep {
        table {
          border-bottom: 0;
          margin-top: 0;
          thead {
            th:last-child {
              width: 30px;
            }
          }
          .spinner-border {
            margin-left: 5px;
            width: 15px;
            height: 15px;
          }
        }
      }
    }
  }

  .visible {
    position: relative;
    z-index: 3;
  }
}
</style>
